import VideoControls from './VideoControls';
import Accordion from '../modules/Accordion';
import Grid from '../modules/Grid';
import GravityFormsValidator from '../modules/GravityFormsValidator';
import ProgressPlayButton from '../modules/ProgressPlayButton';
import ReadMore from '../modules/ReadMore';
import Slider from '../modules/Slider';

/**
 * Content fetched into a modal by ButtonModal.js arrives inert — no listeners
 * bound, no swipers built, nothing measured. ButtonModal fires
 * 'ajaxModalHasLoaded' once the HTML has landed; this re-runs the
 * element-scoped modules over the injected container only, mirroring the
 * bootstrap in child-app.js.
 *
 * Add a module by adding a { selector, init } pair to MODULES below.
 *
 * NOT covered — each needs page-level state the injected markup can't reach:
 *   • modal open/close buttons — ButtonModal binds these once on page load, so
 *                                a close button inside injected content is inert
 *   • lightbox galleries       — likewise bound once by LightboxGallery
 *
 * Imgix and VideoControls partially handle themselves already (they listen
 * for 'ajaxModalHasLoaded' directly), but neither constructs a fresh instance
 * for elements that were never on the page at load time, so they still need
 * to run through here too.
 */
const MODULES = [
    { selector: '[data-module=accordion]', init: (el) => new Accordion(el) },
    { selector: '[data-module=grid]', init: (el) => new Grid(el) },
    { selector: '[data-module=slider]', init: (el) => new Slider(el) },
    { selector: '.reveal-button', init: (el) => new ReadMore(el) },
    { selector: '.o-media-container__media--video-controls', init: (el) => new ProgressPlayButton(el) },
    { selector: 'video[data-controls]', init: (el) => new VideoControls(el) },
    { selector: '.o-form', init: (el) => new GravityFormsValidator({ el }) },
];

export default class AjaxModalContent {
    constructor() {
        // selector -> elements already initialised. ButtonModal replaces the
        // container contents on every load, so this only guards against the
        // event firing more than once for the same markup.
        this.initialised = new Map();

        document.addEventListener('ajaxModalHasLoaded', () => this.init());
    }

    init() {
        document.querySelectorAll('[data-modal-content]').forEach((container) => {
            MODULES.forEach(({ selector, init }) => {
                container.querySelectorAll(selector).forEach((el) => {
                    if (this.hasInitialised(selector, el)) return;

                    try {
                        init(el);
                    } catch (error) {
                        console.error(`Modal content module "${selector}" failed to initialise`, el, error);
                    }
                });
            });
        });
    }

    hasInitialised(selector, el) {
        if (!this.initialised.has(selector)) {
            this.initialised.set(selector, new WeakSet());
        }

        const seen = this.initialised.get(selector);

        if (seen.has(el)) return true;

        seen.add(el);
        return false;
    }
}
